/**
 * Cart Milestones System
 * Ultra-fast, dependency-free milestone tracker for Shopify cart drawer
 * @version 1.0.0
 */

class CartMilestones {
  constructor(container) {
    this.container = container;
    this.config = this.parseConfig();
    this.state = {
      cartData: null,
      currentMilestone: null,
      nextMilestone: null,
      carouselProducts: []
    };
    
    // Debounce cart updates
    this.updateDebounceTimer = null;
    this.fetchAbortController = null;
    
    this.init();
  }

  /**
   * Parse configuration from JSON script tag
   */
  parseConfig() {
    const configEl = this.container.querySelector('[data-milestone-config]');
    if (!configEl) {
      console.error('Cart milestones: Config not found');
      return null;
    }

    const config = JSON.parse(configEl.textContent);
    const settings = {
      storeCurrency: this.container.dataset.storeCurrency,
      countryCode: this.container.dataset.countryCode,
      baseCurrency: this.container.dataset.baseCurrency,
      useAllProducts: this.container.dataset.useAllProducts === 'true',
      carouselLimit: parseInt(this.container.dataset.carouselLimit, 10),
      showRewardCurrency: this.container.dataset.showRewardCurrency === 'true',
      showMicrocopy: this.container.dataset.showMicrocopy === 'true'
    };

    return { ...config, settings };
  }

  /**
   * Initialize the milestone system
   */
  async init() {
    if (!this.config) return;

    // Fetch initial cart data
    await this.fetchCartData();
    
    // Render milestones
    this.renderMilestones();
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Load carousel products (async, non-blocking)
    this.loadCarouselProducts();
  }

  /**
   * Fetch cart data from Shopify
   */
  async fetchCartData() {
    try {
      // Abort any pending fetch
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
      }
      
      this.fetchAbortController = new AbortController();
      
      const response = await fetch('/cart.js', {
        signal: this.fetchAbortController.signal
      });
      
      if (!response.ok) throw new Error('Cart fetch failed');
      
      this.state.cartData = await response.json();
      this.fetchAbortController = null;
      
      return this.state.cartData;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Cart milestones: Fetch error', error);
      }
      return null;
    }
  }

  /**
   * Get eligible milestones for current country and currency
   */
  getEligibleMilestones() {
    const { countryCode, storeCurrency } = this.config.settings;
    
    return this.config.milestones
      .filter(milestone => {
        // Check country eligibility
        const eligibleCountries = milestone.eligibleCountries
          .map(c => c.trim().toUpperCase())
          .filter(c => c.length > 0);
        
        // Empty array = all countries eligible
        if (eligibleCountries.length === 0) return true;
        
        return eligibleCountries.includes(countryCode.toUpperCase());
      })
      .filter(milestone => {
        // Check if threshold exists for current currency
        return milestone.thresholds[storeCurrency] !== undefined;
      })
      .sort((a, b) => a.priority - b.priority);
  }

/**
 * Calculate milestone progress
 */
calculateProgress() {
  if (!this.state.cartData) return null;

  const eligibleMilestones = this.getEligibleMilestones();
  if (eligibleMilestones.length === 0) return null;

  const { storeCurrency } = this.config.settings;
  
  // Calculate total EXCLUDING gift cards
  const eligibleItemsTotal = (this.state.cartData.items || [])
    .filter(item => {
      // Exclude gift cards by checking product type, title, and handle
      const isGiftCard = 
        item.product_type?.toLowerCase().includes('gift card') ||
        item.title?.toLowerCase().includes('gift card') ||
        item.handle?.includes('gift-card');
      
      return !isGiftCard; // Only count non-gift-card items
    })
    .reduce((sum, item) => sum + item.final_line_price, 0);

  console.log('[Milestones] Cart total (excluding gift cards):', (eligibleItemsTotal / 100).toFixed(2));
  
  const cartTotal = eligibleItemsTotal; // Use filtered total instead of full cart total

  // Find current and next milestones
  let currentMilestone = null;
  let nextMilestone = null;

  for (const milestone of eligibleMilestones) {
    const thresholdCents = milestone.thresholds[storeCurrency] * 100;
    
    if (cartTotal >= thresholdCents) {
      currentMilestone = {
        ...milestone,
        threshold: thresholdCents,
        unlocked: true
      };
    } else {
      nextMilestone = {
        ...milestone,
        threshold: thresholdCents,
        unlocked: false,
        remaining: thresholdCents - cartTotal
      };
      break;
    }
  }

    // If cart is above all milestones, show the last one as current
    if (!nextMilestone && currentMilestone) {
      return {
        current: currentMilestone,
        next: null,
        allUnlocked: true
      };
    }

    // If no milestones unlocked, first is next
    if (!currentMilestone && nextMilestone) {
      return {
        current: null,
        next: nextMilestone,
        allUnlocked: false
      };
    }

    // Normal state: show progress to next
    return {
      current: currentMilestone,
      next: nextMilestone,
      allUnlocked: false
    };
  }

/**
 * Format money without currency code
 */
formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  return `$${amount}`;
}

/**
 * Render milestone UI
 */
renderMilestones() {
  const progress = this.calculateProgress();
  const container = this.container.querySelector('[data-milestones-container]');
  
  if (!progress || !container) {
    if (container) {
      container.innerHTML = '<p class="cart-milestones__empty">No milestones available</p>';
    }
    return;
  }

  let html = '';

  // Show current unlocked milestone
  if (progress.current) {
    html += this.renderMilestoneCard(progress.current, true);
  }

  // Show next milestone with progress
  if (progress.next) {
    html += this.renderMilestoneCard(progress.next, false);
  }

  // All milestones unlocked (remove the complete block - CSS hides it anyway)
  if (progress.allUnlocked) {
    html += `
      <div class="cart-milestones__complete">
        <span class="cart-milestones__complete-icon">✨</span>
        <p class="cart-milestones__complete-text">All rewards unlocked!</p>
      </div>
    `;
  }

  container.innerHTML = html;
  
  // Store state for carousel
  this.state.currentMilestone = progress.current;
  this.state.nextMilestone = progress.next;
  
  // Update disclaimer based on state
  this.updateDisclaimer(progress);
  
  // Update carousel if loaded
  if (this.state.carouselProducts.length > 0) {
    this.renderCarousel();
  }
}

/**
 * Update disclaimer text based on milestone state
 */
updateDisclaimer(progress) {
  const disclaimerContainer = this.container.querySelector('[data-disclaimer-container]');
  const disclaimerText = this.container.querySelector('[data-disclaimer-text]');
  
  if (!disclaimerContainer || !disclaimerText) return;
  
  // Hide disclaimer if nothing unlocked yet
  if (!progress.current) {
    disclaimerContainer.style.display = 'none';
    return;
  }
  
  // Show disclaimer
  disclaimerContainer.style.display = 'block';
  
  // Check if all milestones are unlocked
  if (progress.allUnlocked || (!progress.next && progress.current)) {
    // All unlocked - show gift card message
    disclaimerText.textContent = 'Gift card emailed after purchase. Discount codes may affect eligibility.';
  } else {
    // Some unlocked, some not - show standard message
    disclaimerText.textContent = 'Discount codes may affect eligibility at checkout.';
  }
}

  /**
 * Render individual milestone card
 */
renderMilestoneCard(milestone, isUnlocked) {
  const { storeCurrency, showRewardCurrency, baseCurrency } = this.config.settings;
  
  if (isUnlocked) {
    return `
      <div class="cart-milestone cart-milestone--unlocked" data-milestone-id="${milestone.id}">
        <div class="cart-milestone__icon">${milestone.icon}</div>
        <div class="cart-milestone__content">
          <p class="cart-milestone__text cart-milestone__text--unlocked">
            ${milestone.unlockedText.replace('{label}', milestone.label)}
          </p>
        </div>
      </div>
    `;
  }

  // Calculate cart total EXCLUDING gift cards (same logic as calculateProgress)
  const eligibleItemsTotal = (this.state.cartData?.items || [])
    .filter(item => {
      const isGiftCard = 
        item.product_type?.toLowerCase().includes('gift card') ||
        item.title?.toLowerCase().includes('gift card') ||
        item.handle?.includes('gift-card');
      return !isGiftCard;
    })
    .reduce((sum, item) => sum + item.final_line_price, 0);

  // Calculate progress percentage using filtered total
  const progressPercent = Math.min((eligibleItemsTotal / milestone.threshold) * 100, 100);
  const remainingFormatted = this.formatMoney(milestone.remaining);
  
  const awayText = milestone.awayText
    .replace('{amount}', `<strong>${remainingFormatted}</strong>`)
    .replace('{label}', milestone.label.toLowerCase());

  return `
    <div class="cart-milestone" data-milestone-id="${milestone.id}">
      <div class="cart-milestone__header">
        <div class="cart-milestone__icon">${milestone.icon}</div>
        <p class="cart-milestone__text">${awayText}</p>
      </div>
      <div class="cart-milestone__progress-wrapper">
        <div class="cart-milestone__progress-bar">
          <div 
            class="cart-milestone__progress-fill" 
            style="width: ${progressPercent}%"
            data-progress="${progressPercent}"
          ></div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Load products for carousel
 */
async loadCarouselProducts() {
  try {
    const { useAllProducts, carouselLimit } = this.config.settings;
    const { collectionHandle } = this.config;
    
    let url = '/products.json?limit=250';
    
    if (!useAllProducts && collectionHandle) {
      url = `/collections/${collectionHandle}/products.json?limit=250`;
    }

    console.log('[Milestones] Fetching products from:', url);

    const response = await fetch(url);
    if (!response.ok) throw new Error('Products fetch failed');
    
    const data = await response.json();
    this.state.carouselProducts = data.products || [];
    
    console.log('[Milestones] Loaded products:', this.state.carouselProducts.length);
    
    // ALWAYS render carousel after products load
    this.renderCarousel();
    
  } catch (error) {
    console.error('[Milestones] Product fetch error:', error);
  }
}

/**
 * Normalize price to cents (integer)
 * Handles both string prices ("35.00") and cent integers (3500)
 */
normalizePriceToCents(price) {
  if (typeof price === 'string') {
    // String price like "35.00" → parse as float and convert to cents
    const dollars = parseFloat(price);
    return Math.round(dollars * 100);
  }
  
  if (typeof price === 'number') {
    // If already a large number (>= 100), assume it's cents
    // If small number (< 100), assume it's dollars and convert
    if (price >= 100) {
      return Math.round(price);
    } else {
      return Math.round(price * 100);
    }
  }
  
  return 0;
}

/**
 * Smart product ranking for carousel
 * @param {Array} products - Array of product objects
 * @param {Number} remainingCents - Amount needed to reach milestone (in cents)
 * @returns {Array} Ranked products sorted by relevance
 */
rankProductsForMilestone(products, remainingCents) {
  const cartItemIds = (this.state.cartData?.items || []).map(item => item.variant_id);
  
  console.log('[Milestones] Ranking. Remaining:', (remainingCents / 100).toFixed(2), 'Cart variant IDs:', cartItemIds);
  
  // Filter products: exclude gift cards, non-shipping items, and items in cart
  const availableProducts = products.filter(product => {
    // Exclude gift cards by multiple criteria
    const isGiftCard = 
      product.requires_shipping === false ||
      (product.product_type && product.product_type.toLowerCase().includes('gift card')) ||
      (product.handle && product.handle.includes('gift-card')) ||
      (product.title && product.title.toLowerCase().includes('gift card'));
    
    if (isGiftCard) {
      console.log('[Milestones] Excluding gift card:', product.title);
      return false;
    }

    // Exclude $20 Jute Bag (keep the $35 Jute Beach Tote Bag)
if (product.handle === 'jute-bag') {
  console.log('[Milestones] Excluding $20 jute bag:', product.title);
  return false;
}
    
    // Must have available variant not in cart
    const hasAvailableVariant = product.variants.some(v => 
      v.available && !cartItemIds.includes(v.id)
    );
    
    return hasAvailableVariant;
  });

  console.log('[Milestones] Available products (after gift card filter):', availableProducts.length);

  // Map each product to its cheapest available variant (normalized to cents)
  const productsWithPrice = availableProducts.map(product => {
    const availableVariants = product.variants.filter(v => 
      v.available && !cartItemIds.includes(v.id)
    );
    
    // Normalize all variant prices to cents
    const variantsWithCents = availableVariants.map(v => ({
      ...v,
      priceCents: this.normalizePriceToCents(v.price)
    }));
    
    // Find cheapest variant in cents
    const cheapestVariant = variantsWithCents.reduce((min, v) => 
      v.priceCents < min.priceCents ? v : min
    , variantsWithCents[0]);

    const priceCents = cheapestVariant.priceCents;
    const overAmount = priceCents - remainingCents;
    const underAmount = remainingCents - priceCents;

    return {
      product,
      variant: cheapestVariant,
      priceCents,
      overAmount,
      underAmount
    };
  });

  console.log('[Milestones] Products with normalized prices:', productsWithPrice.map(p => ({
    title: p.product.title,
    priceCents: p.priceCents,
    priceDisplay: (p.priceCents / 100).toFixed(2)
  })));

  // Sort by ranking rules
  const sorted = productsWithPrice.sort((a, b) => {
    const aUnlocks = a.overAmount >= 0;
    const bUnlocks = b.overAmount >= 0;
    
    // 1. Products that unlock milestone come first
    if (aUnlocks && !bUnlocks) return -1;
    if (!aUnlocks && bUnlocks) return 1;
    
    // 2. Among unlocking products, prefer smallest overspend
    if (aUnlocks && bUnlocks) {
      const overspendDiff = a.overAmount - b.overAmount;
      
      // Tie-breaker: If overspend within $1 (100 cents), apply merchandising priority
      if (Math.abs(overspendDiff) <= 100) {
        const priorityDiff = this.getProductPriority(a.product.handle) - this.getProductPriority(b.product.handle);
        if (priorityDiff !== 0) return priorityDiff;
      }
      
      return overspendDiff;
    }
    
    // 3. Among non-unlocking, prefer closest to threshold
    const underDiff = a.underAmount - b.underAmount;
    
    // Tie-breaker: If underAmount within $1 (100 cents), apply merchandising priority
    if (Math.abs(underDiff) <= 100) {
      const priorityDiff = this.getProductPriority(a.product.handle) - this.getProductPriority(b.product.handle);
      if (priorityDiff !== 0) return priorityDiff;
    }
    
    return underDiff;
  });

  console.log('[Milestones] Ranked products:', sorted.length);

  return sorted;
}

/**
 * Get merchandising priority for tie-breaker
 * Lower number = higher priority
 * Only used when products are within $1 of each other
 */
getProductPriority(handle) {
  const priorityMap = {
    'bronze-shimmering-body-oil': 1,      // Highest priority
    'jute-beach-tote-bag': 2,             // Second priority
    'gold-shimmering-body-oil': 3,        // Third priority
    'rose-gold-shimmering-body-oil': 4    // Fourth priority
  };
  
  // Return priority if found, otherwise 999 (low priority)
  return priorityMap[handle] || 999;
}

/**
 * Render product carousel (1 product at a time, max 3)
 */
renderCarousel() {
  const carouselContainer = this.container.querySelector('[data-carousel-container]');
  
  console.log('[Milestones] renderCarousel called');
  
  if (!this.state.nextMilestone || !carouselContainer) {
    console.log('[Milestones] Carousel hidden - no next milestone');
    if (carouselContainer) {
      carouselContainer.innerHTML = '';
    }
    return;
  }

  const rankedProducts = this.rankProductsForMilestone(
    this.state.carouselProducts,
    this.state.nextMilestone.remaining
  );

  console.log('[Milestones] Ranked products:', rankedProducts.length);

  if (rankedProducts.length === 0) {
    console.log('[Milestones] No products to show in carousel');
    carouselContainer.innerHTML = '';
    return;
  }

  // Show max 3 products
  const productsToShow = rankedProducts.slice(0, 3);
  
  // Initialize current index if not set
  if (!this.state.carouselIndex) {
    this.state.carouselIndex = 0;
  }
  
  // Ensure index is within bounds
  if (this.state.carouselIndex >= productsToShow.length) {
    this.state.carouselIndex = 0;
  }

  const currentProduct = productsToShow[this.state.carouselIndex];
  const showNavigation = productsToShow.length > 1;

  console.log('[Milestones] Showing product', this.state.carouselIndex + 1, 'of', productsToShow.length);

const html = `
  <div class="cart-milestones__carousel-header">
    <h3 class="cart-milestones__carousel-title">Add to unlock reward</h3>
  </div>
  <div class="cart-milestones__carousel-wrapper">
    ${showNavigation ? `
      <button type="button" class="cart-milestones__carousel-nav cart-milestones__carousel-nav--prev" data-carousel-prev aria-label="Previous product">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    ` : ''}
    
    <div class="cart-milestones__carousel-single">
      ${this.renderCarouselItemCompact(currentProduct)}
    </div>
    
    ${showNavigation ? `
      <button type="button" class="cart-milestones__carousel-nav cart-milestones__carousel-nav--next" data-carousel-next aria-label="Next product">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    ` : ''}
  </div>
`;

  carouselContainer.innerHTML = html;
  
  console.log('[Milestones] Carousel HTML updated');
  
  // Attach listeners
  this.attachCarouselNavigation(productsToShow.length);
  this.attachCarouselListeners();
}

/**
 * Render compact horizontal carousel item
 */
renderCarouselItemCompact({ product, variant, priceCents }) {
  const priceFormatted = this.formatMoney(priceCents);
  const imageSrc = product.images[0]?.src || '';

  return `
    <div class="cart-milestones__carousel-card">
      <div class="cart-milestones__carousel-card-image">
        <img 
          src="${imageSrc}" 
          alt="${product.title}"
          loading="lazy"
        />
      </div>
      <div class="cart-milestones__carousel-card-info">
        <h4 class="cart-milestones__carousel-card-title">${product.title}</h4>
        <p class="cart-milestones__carousel-card-price">${priceFormatted}</p>
      </div>
      <button 
        class="cart-milestones__add-btn cart-milestones__add-btn--compact"
        data-variant-id="${variant.id}"
        data-product-title="${product.title}"
      >
        Add
      </button>
    </div>
  `;
}

/**
 * Attach carousel navigation listeners
 */
attachCarouselNavigation(totalProducts) {
  const prevBtn = this.container.querySelector('[data-carousel-prev]');
  const nextBtn = this.container.querySelector('[data-carousel-next]');
  
  if (prevBtn) {
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.state.carouselIndex = (this.state.carouselIndex - 1 + totalProducts) % totalProducts;
      this.renderCarousel();
    });
  }
  
  if (nextBtn) {
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.state.carouselIndex = (this.state.carouselIndex + 1) % totalProducts;
      this.renderCarousel();
    });
  }
}

/**
 * Render individual carousel item
 */
renderCarouselItem({ product, variant, priceCents, overAmount }) {
  // Format price from cents to currency
  const priceFormatted = this.formatMoney(priceCents);
  const imageSrc = product.images[0]?.src || '';
  const willUnlock = overAmount >= 0;

  return `
    <div class="cart-milestones__carousel-item">
      <div class="cart-milestones__carousel-image-wrapper">
        <img 
          src="${imageSrc}" 
          alt="${product.title}"
          class="cart-milestones__carousel-image"
          loading="lazy"
        />
        ${willUnlock ? '<span class="cart-milestones__carousel-badge">Unlocks reward</span>' : ''}
      </div>
      <div class="cart-milestones__carousel-info">
        <h4 class="cart-milestones__carousel-product-title">${product.title}</h4>
        <p class="cart-milestones__carousel-price">${priceFormatted}</p>
        <button 
          class="cart-milestones__add-btn"
          data-variant-id="${variant.id}"
          data-product-title="${product.title}"
        >
          Add to cart
        </button>
      </div>
    </div>
  `;
}

/**
 * Attach event listeners to carousel add buttons
 */
attachCarouselListeners() {
  const buttons = this.container.querySelectorAll('.cart-milestones__add-btn');
  
  buttons.forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await this.handleAddToCart(btn);
    });
  });
}

/**
 * Handle AJAX add to cart
 */
async handleAddToCart(button) {
  const variantId = button.dataset.variantId;
  
  // Disable button
  button.disabled = true;
  const originalText = button.textContent;
  button.textContent = 'Adding...';

  try {
    // Create a hidden form and submit it
    // This triggers your theme's normal add-to-cart flow
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/cart/add';
    form.style.display = 'none';
    
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'id';
    input.value = variantId;
    
    const qtyInput = document.createElement('input');
    qtyInput.type = 'hidden';
    qtyInput.name = 'quantity';
    qtyInput.value = '1';
    
    form.appendChild(input);
    form.appendChild(qtyInput);
    document.body.appendChild(form);
    
    // Mark this form so atc-drawer.js can intercept it
    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.setAttribute('data-atc', '');
    form.appendChild(submitButton);
    
    // Trigger click (this will be intercepted by atc-drawer.js)
    submitButton.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(form);
    }, 100);
    
    // Wait for drawer refresh, then update milestones AND header cart count
    setTimeout(async () => {
      await this.fetchCartData();
      this.renderMilestones();
      this.state.carouselIndex = 0;
      
      // Refresh header cart count bubble
      await this.refreshHeaderCartCount();
      
      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1000);
    }, 800);

  } catch (error) {
    console.error('[Milestones] Add to cart error:', error);
    button.textContent = 'Error';
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 2000);
  }
}

/**
 * Refresh header cart count bubble
 */
async refreshHeaderCartCount() {
  try {
    // Fetch fresh header section
    const response = await fetch('/?sections=header-group', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store'
    });

    if (!response.ok) return;

    const sections = await response.json();
    const headerHTML = sections['header-group'];
    
    if (!headerHTML) return;

    // Parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(headerHTML, 'text/html');
    
    // Find fresh cart bubble
    const freshBubble = doc.querySelector('.cart-bubble') || 
                        doc.querySelector('[data-cart-count]') ||
                        doc.querySelector('.header__icon--cart .cart-count');
    
    // Find current cart bubble
    const currentBubble = document.querySelector('.cart-bubble') || 
                          document.querySelector('[data-cart-count]') ||
                          document.querySelector('.header__icon--cart .cart-count');
    
    if (freshBubble && currentBubble) {
      // Update the count
      currentBubble.textContent = freshBubble.textContent;
      currentBubble.innerHTML = freshBubble.innerHTML;
      console.log('[Milestones] Header cart count updated');
    }
  } catch (error) {
    console.error('[Milestones] Failed to refresh header cart count:', error);
  }
}

/**
 * Handle cart update (debounced and smart)
 */
async handleCartUpdate() {
  clearTimeout(this.updateDebounceTimer);
  
  this.updateDebounceTimer = setTimeout(async () => {
    const oldTotal = this.state.cartData?.total_price || 0;
    const oldItemCount = this.state.cartData?.item_count || 0;
    
    await this.fetchCartData();
    
    const newTotal = this.state.cartData?.total_price || 0;
    const newItemCount = this.state.cartData?.item_count || 0;
    
    // Only re-render if something meaningful changed
    const totalChanged = oldTotal !== newTotal;
    const itemCountChanged = oldItemCount !== newItemCount;
    
    if (totalChanged || itemCountChanged || oldTotal === 0) {
      console.log('[Milestones] Rendering - Total:', oldTotal, '→', newTotal);
      this.renderMilestones();
    } else {
      console.log('[Milestones] Skipped render - no changes');
    }
  }, 600); // Increased to 600ms for more stability
}

  /**
   * Setup event listeners
   */
setupEventListeners() {
    // Listen for cart updates from other parts of the theme
    document.addEventListener('cart:updated', () => this.handleCartUpdate());
    
    // Listen for drawer open (if your theme dispatches this)
    document.addEventListener('cart-drawer:open', () => this.handleCartUpdate());
    
    // IMPORTANT: Also listen for cart refresh events (catches item removal)
    document.addEventListener('cart:refresh', () => this.handleCartUpdate());
    document.addEventListener('cart:change', () => this.handleCartUpdate());
    
    // Fallback: MutationObserver on cart drawer
    const cartDrawer = document.querySelector('[data-cart-drawer]') || 
                       document.querySelector('.cart-drawer') ||
                       document.querySelector('cart-drawer-component') ||
                       document.querySelector('dialog.cart-drawer__dialog') ||
                       this.container.closest('[data-cart]');
    
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        // Only update if cart items changed, not milestone UI
        const hasCartItemChange = mutations.some(mutation => {
          // Check for added nodes
          const hasAdded = Array.from(mutation.addedNodes).some(node => 
            node.nodeType === 1 && 
            (node.matches?.('[data-cart-item]') || node.querySelector?.('[data-cart-item]'))
          );
          
          // Check for removed nodes (item deletion)
          const hasRemoved = Array.from(mutation.removedNodes).some(node => 
            node.nodeType === 1 && 
            (node.matches?.('[data-cart-item]') || node.querySelector?.('[data-cart-item]'))
          );
          
          return hasAdded || hasRemoved;
        });
        
        if (hasCartItemChange) {
          this.handleCartUpdate();
        }
      });
      
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }
  }
  
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartMilestones);
} else {
  initCartMilestones();
}

function initCartMilestones() {
  const containers = document.querySelectorAll('[data-cart-milestones]');
  containers.forEach(container => {
    new CartMilestones(container);
  });
}

// Make it globally available for re-init after drawer refresh
window.initCartMilestones = initCartMilestones;

// Intercept all fetch requests to detect cart updates
(function() {
  const originalFetch = window.fetch;
  let lastCartUpdate = 0;
  
  window.fetch = function(...args) {
    const url = args[0];
    
    // Check if this is a cart MUTATION request (not just reading)
    const isCartMutation = typeof url === 'string' && (
      url.includes('/cart/add') ||
      url.includes('/cart/update') ||
      url.includes('/cart/change')
    );
    
    // Call original fetch
    const fetchPromise = originalFetch.apply(this, args);
    
    // Only trigger updates for mutations, not reads
    if (isCartMutation) {
      const now = Date.now();
      
      // Prevent duplicate triggers within 1 second
      if (now - lastCartUpdate < 1000) {
        console.log('[Milestones] Skipped duplicate trigger');
        return fetchPromise;
      }
      
      lastCartUpdate = now;
      
      fetchPromise.then(response => {
        // Clone response so it can still be used by the theme
        const clonedResponse = response.clone();
        
        // Wait for cart to update, then trigger milestone refresh
        setTimeout(() => {
          console.log('[Milestones] Cart mutation detected:', url);
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }, 400);
        
        return response;
      }).catch(err => {
        return Promise.reject(err);
      });
    }
    
    return fetchPromise;
  };
  
  console.log('[Milestones] Fetch interceptor installed (smart mode)');
})();